import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
    Video, Eye, Trash2, RefreshCw, CheckCircle, Clock, AlertCircle,
    Loader2, Star, TrendingUp, Search, Filter, BarChart3, User,
    Play, Share2, Copy, ExternalLink, Download, Mail, MessageSquare,
    Sparkles, Bone
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VideoAnalysisTab = ({ sports = [] }) => {
    const navigate = useNavigate();
    const [analyses, setAnalyses] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [showDetailDialog, setShowDetailDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [analysisToDelete, setAnalysisToDelete] = useState(null);
    const [detailTab, setDetailTab] = useState('analysis');
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [demoLoading, setDemoLoading] = useState(false);
    const [demoStatus, setDemoStatus] = useState(null);
    
    // Filters
    const [filters, setFilters] = useState({
        sport: 'all',
        status: 'all',
        minScore: ''
    });
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadData();
        loadDemoStatus();
    }, [filters]);

    const loadDemoStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/api/demo/status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDemoStatus(res.data);
        } catch (e) {
            console.warn('Demo status fetch failed', e);
        }
    };

    const handleSeedDemo = async () => {
        setDemoLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/api/demo/seed`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Demo data ready! Opening biomechanics view...');
            setDemoStatus({
                has_demo_data: true,
                demo_video_id: res.data.video_id,
                demo_url: res.data.demo_url
            });
            // Navigate to biomechanics page
            window.open(res.data.demo_url, '_blank');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to seed demo');
        } finally {
            setDemoLoading(false);
        }
    };

    const handleOpenDemo = () => {
        if (demoStatus?.demo_url) {
            window.open(demoStatus.demo_url, '_blank');
        }
    };

    const handleClearDemo = async () => {
        if (!window.confirm('Clear all demo data?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/demo/clear`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Demo data cleared');
            setDemoStatus({ has_demo_data: false });
        } catch (e) {
            toast.error('Failed to clear demo');
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = {};
            if (filters.sport !== 'all') params.sport = filters.sport;
            if (filters.status !== 'all') params.status = filters.status;
            if (filters.minScore) params.min_score = parseFloat(filters.minScore);

            const [analysesRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/api/video-analysis/all`, { 
                    headers: { Authorization: `Bearer ${token}` },
                    params 
                }),
                axios.get(`${API_URL}/api/video-analysis/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            
            setAnalyses(analysesRes.data.analyses || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Failed to load video analyses:', error);
            toast.error('Failed to load video analyses');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!analysisToDelete) return;
        
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/video-analysis/${analysisToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Video analysis deleted');
            setShowDeleteDialog(false);
            setAnalysisToDelete(null);
            loadData();
        } catch (error) {
            console.error('Failed to delete:', error);
            toast.error('Failed to delete video analysis');
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Completed
                </span>;
            case 'processing':
                return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Processing
                </span>;
            case 'pending':
                return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Pending
                </span>;
            case 'failed':
                return <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Failed
                </span>;
            default:
                return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full">{status}</span>;
        }
    };

    const getPotentialBadge = (rating) => {
        const colors = {
            'Elite Prospect': 'bg-purple-500/20 text-purple-400',
            'High Potential': 'bg-green-500/20 text-green-400',
            'Developing Talent': 'bg-blue-500/20 text-blue-400',
            'Needs Work': 'bg-yellow-500/20 text-yellow-400',
            'Early Stage': 'bg-gray-500/20 text-gray-400'
        };
        return <span className={`px-2 py-1 text-xs rounded-full ${colors[rating] || colors['Early Stage']}`}>
            {rating}
        </span>;
    };

    const filteredAnalyses = analyses.filter(a => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            a.athlete_name?.toLowerCase().includes(query) ||
            a.sport?.toLowerCase().includes(query)
        );
    });

    const availableSports = [...new Set(analyses.map(a => a.sport).filter(Boolean))];

    return (
        <div className="space-y-6">
            {/* Quick Demo Banner */}
            <div className="bg-gradient-to-r from-purple-500/10 via-cyan-500/10 to-purple-500/10 border border-purple-500/30 rounded-lg p-4 md:p-5">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-purple-500/20 rounded-lg">
                            <Sparkles className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                            <h3 className="font-bold text-base flex items-center gap-2">
                                Quick Demo Mode
                                {demoStatus?.has_demo_data && (
                                    <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                                        Ready
                                    </span>
                                )}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-1">
                                Pre-processed AI biomechanics + player tracking demo for presentations.
                                {' '}Showcases skeleton overlay, heatmaps, and injury risk without uploads.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {demoStatus?.has_demo_data ? (
                            <>
                                <Button
                                    onClick={handleOpenDemo}
                                    className="bg-purple-500 hover:bg-purple-600 text-white"
                                    size="sm"
                                    data-testid="open-demo-btn"
                                >
                                    <Play className="w-4 h-4 mr-2" />
                                    Open Demo
                                </Button>
                                <Button
                                    onClick={handleSeedDemo}
                                    variant="outline"
                                    size="sm"
                                    className="border-white/10"
                                    disabled={demoLoading}
                                    data-testid="reseed-demo-btn"
                                >
                                    {demoLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                                    Reseed
                                </Button>
                                <Button
                                    onClick={handleClearDemo}
                                    variant="outline"
                                    size="sm"
                                    className="border-red-500/30 text-red-400"
                                    data-testid="clear-demo-btn"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={handleSeedDemo}
                                className="bg-purple-500 hover:bg-purple-600 text-white"
                                size="sm"
                                disabled={demoLoading}
                                data-testid="seed-demo-btn"
                            >
                                {demoLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bone className="w-4 h-4 mr-2" />}
                                Launch Quick Demo
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-[#0d2847] rounded-lg p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                        <Video className="w-5 h-5 text-primary" />
                        <span className="text-sm text-muted-foreground">Total</span>
                    </div>
                    <p className="text-2xl font-bold">{stats?.total_videos || 0}</p>
                </div>
                <div className="bg-[#0d2847] rounded-lg p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-5 h-5 text-blue-400" />
                        <span className="text-sm text-muted-foreground">Pending</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-400">{stats?.pending || 0}</p>
                </div>
                <div className="bg-[#0d2847] rounded-lg p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                        <Loader2 className="w-5 h-5 text-yellow-400" />
                        <span className="text-sm text-muted-foreground">Processing</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-400">{stats?.processing || 0}</p>
                </div>
                <div className="bg-[#0d2847] rounded-lg p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-sm text-muted-foreground">Completed</span>
                    </div>
                    <p className="text-2xl font-bold text-green-400">{stats?.completed || 0}</p>
                </div>
                <div className="bg-[#0d2847] rounded-lg p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="w-5 h-5 text-purple-400" />
                        <span className="text-sm text-muted-foreground">Avg Score</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-400">
                        {stats?.by_sport && Object.keys(stats.by_sport).length > 0
                            ? Math.round(Object.values(stats.by_sport).reduce((a, b) => a + b.avg_score, 0) / Object.keys(stats.by_sport).length)
                            : '-'}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-[#0d2847] rounded-lg p-4 border border-white/5">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by athlete or sport..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 bg-[#0a1628] border-white/10"
                            />
                        </div>
                    </div>
                    
                    <Select value={filters.sport} onValueChange={(v) => setFilters({...filters, sport: v})}>
                        <SelectTrigger className="w-[150px] bg-[#0a1628] border-white/10">
                            <SelectValue placeholder="Sport" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Sports</SelectItem>
                            {availableSports.map(sport => (
                                <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
                        <SelectTrigger className="w-[150px] bg-[#0a1628] border-white/10">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="processing">Processing</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                        </SelectContent>
                    </Select>

                    <Input
                        type="number"
                        placeholder="Min Score"
                        value={filters.minScore}
                        onChange={(e) => setFilters({...filters, minScore: e.target.value})}
                        className="w-[120px] bg-[#0a1628] border-white/10"
                    />

                    <Button variant="outline" size="sm" onClick={loadData} className="border-white/10">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Analyses Table */}
            <div className="bg-[#0d2847] rounded-lg border border-white/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-white/5 bg-[#0a1628]">
                                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Athlete</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Sport</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Position</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Status</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Score</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Potential</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Date</th>
                                <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-12 text-center">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                                    </td>
                                </tr>
                            ) : filteredAnalyses.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-12 text-center text-muted-foreground">
                                        No video analyses found
                                    </td>
                                </tr>
                            ) : (
                                filteredAnalyses.map((analysis) => (
                                    <tr key={analysis.id} className="border-b border-white/5 hover:bg-white/5">
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-2">
                                                <User className="w-4 h-4 text-muted-foreground" />
                                                <span className="font-medium">{analysis.athlete_name || 'Unknown'}</span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded">
                                                {analysis.sport}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-sm text-muted-foreground">
                                            {analysis.position || '-'}
                                        </td>
                                        <td className="py-3 px-4">
                                            {getStatusBadge(analysis.analysis_status)}
                                        </td>
                                        <td className="py-3 px-4">
                                            {analysis.overall_score ? (
                                                <span className={`font-bold ${
                                                    analysis.overall_score >= 80 ? 'text-green-400' :
                                                    analysis.overall_score >= 60 ? 'text-yellow-400' :
                                                    'text-red-400'
                                                }`}>
                                                    {analysis.overall_score}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="py-3 px-4">
                                            {analysis.potential_rating ? getPotentialBadge(analysis.potential_rating) : '-'}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-muted-foreground">
                                            {new Date(analysis.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        window.open(`/video-analysis/${analysis.id}/biomechanics`, '_blank');
                                                    }}
                                                    title="Body Tracking & Analytics"
                                                    data-testid={`biomech-${analysis.id}`}
                                                >
                                                    <Bone className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedAnalysis(analysis);
                                                        setShowDetailDialog(true);
                                                    }}
                                                    data-testid={`view-analysis-${analysis.id}`}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setAnalysisToDelete(analysis);
                                                        setShowDeleteDialog(true);
                                                    }}
                                                    className="text-red-400 hover:text-red-300"
                                                    data-testid={`delete-analysis-${analysis.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail Dialog with Tabs */}
            <Dialog open={showDetailDialog} onOpenChange={(open) => {
                setShowDetailDialog(open);
                if (!open) setDetailTab('analysis');
            }}>
                <DialogContent className="max-w-3xl bg-[#0d2847] border-white/10 max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <div className="flex items-center justify-between">
                            <DialogTitle className="flex items-center gap-2">
                                <Video className="w-5 h-5 text-primary" />
                                {selectedAnalysis?.athlete_name} - {selectedAnalysis?.sport}
                            </DialogTitle>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowShareDialog(true)}
                                className="border-white/10"
                            >
                                <Share2 className="w-4 h-4 mr-2" />
                                Share
                            </Button>
                        </div>
                    </DialogHeader>
                    
                    {selectedAnalysis && (
                        <Tabs value={detailTab} onValueChange={setDetailTab} className="w-full">
                            <TabsList className="grid w-full grid-cols-2 bg-[#0a1628]">
                                <TabsTrigger value="analysis" className="data-[state=active]:bg-primary">
                                    <BarChart3 className="w-4 h-4 mr-2" />
                                    Analysis Results
                                </TabsTrigger>
                                <TabsTrigger value="video" className="data-[state=active]:bg-primary">
                                    <Play className="w-4 h-4 mr-2" />
                                    Watch Video
                                </TabsTrigger>
                            </TabsList>

                            {/* Analysis Tab */}
                            <TabsContent value="analysis" className="space-y-4 mt-4">
                                {/* Header Info */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-[#0a1628] rounded-lg p-4">
                                        <p className="text-xs text-muted-foreground mb-1">Athlete</p>
                                        <p className="font-medium">{selectedAnalysis.athlete_name}</p>
                                    </div>
                                    <div className="bg-[#0a1628] rounded-lg p-4">
                                        <p className="text-xs text-muted-foreground mb-1">Sport</p>
                                        <p className="font-medium">{selectedAnalysis.sport}</p>
                                    </div>
                                    <div className="bg-[#0a1628] rounded-lg p-4">
                                        <p className="text-xs text-muted-foreground mb-1">Position</p>
                                        <p className="font-medium">{selectedAnalysis.position || 'Not specified'}</p>
                                    </div>
                                    <div className="bg-[#0a1628] rounded-lg p-4">
                                        <p className="text-xs text-muted-foreground mb-1">Status</p>
                                        {getStatusBadge(selectedAnalysis.analysis_status)}
                                    </div>
                                </div>

                                {/* Scores */}
                                {selectedAnalysis.analysis_status === 'completed' && (
                                    <>
                                        <div className="bg-[#0a1628] rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="font-medium flex items-center gap-2">
                                                    <Star className="w-4 h-4 text-yellow-400" />
                                                    Overall Score
                                                </h4>
                                                <span className={`text-3xl font-bold ${
                                                    selectedAnalysis.overall_score >= 80 ? 'text-green-400' :
                                                    selectedAnalysis.overall_score >= 60 ? 'text-yellow-400' :
                                                    'text-red-400'
                                                }`}>
                                                    {selectedAnalysis.overall_score}/100
                                                </span>
                                            </div>
                                            {selectedAnalysis.potential_rating && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-muted-foreground">Potential:</span>
                                                    {getPotentialBadge(selectedAnalysis.potential_rating)}
                                                </div>
                                            )}
                                        </div>

                                        {/* Metrics */}
                                        {selectedAnalysis.metrics && (
                                            <div className="bg-[#0a1628] rounded-lg p-4">
                                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                                    <BarChart3 className="w-4 h-4 text-primary" />
                                                    Performance Metrics
                                                </h4>
                                                <div className="grid grid-cols-2 gap-3">
                                                    {Object.entries(selectedAnalysis.metrics).map(([key, value]) => (
                                                        <div key={key} className="flex items-center justify-between">
                                                            <span className="text-sm text-muted-foreground">{key}</span>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                                                                    <div 
                                                                        className={`h-full rounded-full ${
                                                                            value >= 80 ? 'bg-green-500' :
                                                                            value >= 60 ? 'bg-yellow-500' :
                                                                            'bg-red-500'
                                                                        }`}
                                                                        style={{ width: `${value}%` }}
                                                                    />
                                                                </div>
                                                                <span className="text-sm font-medium w-8">{value}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Strengths & Areas for Improvement */}
                                        <div className="grid grid-cols-2 gap-4">
                                            {selectedAnalysis.strengths && (
                                                <div className="bg-[#0a1628] rounded-lg p-4">
                                                    <h4 className="font-medium mb-2 text-green-400">Strengths</h4>
                                                    <ul className="space-y-1">
                                                        {selectedAnalysis.strengths.map((s, i) => (
                                                            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                                                <CheckCircle className="w-3 h-3 mt-1 text-green-400 flex-shrink-0" />
                                                                {s}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {selectedAnalysis.areas_for_improvement && (
                                                <div className="bg-[#0a1628] rounded-lg p-4">
                                                    <h4 className="font-medium mb-2 text-yellow-400">Areas to Improve</h4>
                                                    <ul className="space-y-1">
                                                        {selectedAnalysis.areas_for_improvement.map((a, i) => (
                                                            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                                                <TrendingUp className="w-3 h-3 mt-1 text-yellow-400 flex-shrink-0" />
                                                                {a}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>

                                        {/* Detailed Analysis */}
                                        {selectedAnalysis.detailed_analysis && (
                                            <div className="bg-[#0a1628] rounded-lg p-4">
                                                <h4 className="font-medium mb-2">Detailed Analysis</h4>
                                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                                    {selectedAnalysis.detailed_analysis}
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Timestamps */}
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>Uploaded: {new Date(selectedAnalysis.created_at).toLocaleString()}</span>
                                    {selectedAnalysis.analyzed_at && (
                                        <span>Analyzed: {new Date(selectedAnalysis.analyzed_at).toLocaleString()}</span>
                                    )}
                                </div>
                            </TabsContent>

                            {/* Video Tab */}
                            <TabsContent value="video" className="space-y-4 mt-4">
                                <div className="bg-[#0a1628] rounded-lg p-4">
                                    <div className="aspect-video bg-black rounded-lg overflow-hidden">
                                        {selectedAnalysis.video_url ? (
                                            <video
                                                controls
                                                className="w-full h-full"
                                                poster="/video-placeholder.jpg"
                                            >
                                                <source src={`${API_URL}${selectedAnalysis.video_url}`} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        ) : (
                                            <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                                                <Video className="w-16 h-16 mb-4 opacity-50" />
                                                <p>Video not available</p>
                                                <p className="text-xs mt-1">(Demo video - actual video upload required)</p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Video Info */}
                                    <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-muted-foreground">Original Filename</p>
                                            <p className="font-medium">{selectedAnalysis.original_filename || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">File Size</p>
                                            <p className="font-medium">
                                                {selectedAnalysis.file_size 
                                                    ? `${(selectedAnalysis.file_size / (1024 * 1024)).toFixed(2)} MB`
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                        {selectedAnalysis.video_description && (
                                            <div className="col-span-2">
                                                <p className="text-muted-foreground">Description</p>
                                                <p className="font-medium">{selectedAnalysis.video_description}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="border-white/10">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Share Dialog */}
            <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Share2 className="w-5 h-5 text-primary" />
                            Share Player Profile & Analysis
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedAnalysis && (
                        <div className="space-y-4">
                            {/* Preview Card */}
                            <div className="bg-[#0a1628] rounded-lg p-4">
                                <div className="flex items-center gap-4 mb-3">
                                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                                        <User className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <p className="font-bold">{selectedAnalysis.athlete_name}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {selectedAnalysis.sport} • {selectedAnalysis.position || 'Player'}
                                        </p>
                                    </div>
                                    {selectedAnalysis.overall_score && (
                                        <div className="ml-auto text-right">
                                            <p className={`text-2xl font-bold ${
                                                selectedAnalysis.overall_score >= 80 ? 'text-green-400' :
                                                selectedAnalysis.overall_score >= 60 ? 'text-yellow-400' :
                                                'text-red-400'
                                            }`}>
                                                {selectedAnalysis.overall_score}
                                            </p>
                                            <p className="text-xs text-muted-foreground">Score</p>
                                        </div>
                                    )}
                                </div>
                                {selectedAnalysis.potential_rating && (
                                    <div className="flex items-center gap-2">
                                        {getPotentialBadge(selectedAnalysis.potential_rating)}
                                        <span className="text-xs text-muted-foreground">AI Assessment</span>
                                    </div>
                                )}
                            </div>

                            {/* Share Options */}
                            <div className="space-y-2">
                                <p className="text-sm font-medium">Share via</p>
                                
                                {/* Copy Link */}
                                <Button
                                    variant="outline"
                                    className="w-full justify-start border-white/10"
                                    onClick={() => {
                                        const shareUrl = `${window.location.origin}/video-analysis/${selectedAnalysis.id}`;
                                        navigator.clipboard.writeText(shareUrl);
                                        toast.success('Link copied to clipboard!');
                                    }}
                                >
                                    <Copy className="w-4 h-4 mr-3" />
                                    Copy Profile Link
                                </Button>

                                {/* Email */}
                                <Button
                                    variant="outline"
                                    className="w-full justify-start border-white/10"
                                    onClick={() => {
                                        const subject = encodeURIComponent(`Check out ${selectedAnalysis.athlete_name}'s Sports Analysis`);
                                        const body = encodeURIComponent(
                                            `Hi,\n\nI wanted to share ${selectedAnalysis.athlete_name}'s sports performance analysis with you.\n\n` +
                                            `Sport: ${selectedAnalysis.sport}\n` +
                                            `Position: ${selectedAnalysis.position || 'N/A'}\n` +
                                            `Overall Score: ${selectedAnalysis.overall_score || 'Pending'}/100\n` +
                                            `Potential: ${selectedAnalysis.potential_rating || 'N/A'}\n\n` +
                                            `View full analysis: ${window.location.origin}/video-analysis/${selectedAnalysis.id}\n\n` +
                                            `- Shared from NextGen Tournaments`
                                        );
                                        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                                    }}
                                >
                                    <Mail className="w-4 h-4 mr-3" />
                                    Share via Email
                                </Button>

                                {/* WhatsApp */}
                                <Button
                                    variant="outline"
                                    className="w-full justify-start border-white/10"
                                    onClick={() => {
                                        const text = encodeURIComponent(
                                            `🏆 Check out ${selectedAnalysis.athlete_name}'s Sports Analysis!\n\n` +
                                            `⚽ Sport: ${selectedAnalysis.sport}\n` +
                                            `📊 Score: ${selectedAnalysis.overall_score || 'Pending'}/100\n` +
                                            `⭐ Potential: ${selectedAnalysis.potential_rating || 'N/A'}\n\n` +
                                            `View full analysis: ${window.location.origin}/video-analysis/${selectedAnalysis.id}`
                                        );
                                        window.open(`https://wa.me/?text=${text}`, '_blank');
                                    }}
                                >
                                    <MessageSquare className="w-4 h-4 mr-3" />
                                    Share on WhatsApp
                                </Button>

                                {/* Open in New Tab */}
                                <Button
                                    variant="outline"
                                    className="w-full justify-start border-white/10"
                                    onClick={() => {
                                        window.open(`/video-analysis/${selectedAnalysis.id}`, '_blank');
                                    }}
                                >
                                    <ExternalLink className="w-4 h-4 mr-3" />
                                    Open in New Tab
                                </Button>
                            </div>

                            {/* Download Report (Future) */}
                            <div className="pt-4 border-t border-white/10">
                                <Button
                                    variant="outline"
                                    className="w-full border-primary/50 text-primary"
                                    disabled
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download PDF Report (Coming Soon)
                                </Button>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowShareDialog(false)} className="border-white/10">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-red-400">Delete Video Analysis</DialogTitle>
                    </DialogHeader>
                    <p className="text-muted-foreground">
                        Are you sure you want to delete this video analysis for{' '}
                        <span className="text-white font-medium">{analysisToDelete?.athlete_name}</span>?
                        This action cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-white/10">
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default VideoAnalysisTab;
